import {
  createTheme, defaultVariantColorsResolver,
  type MantineColorsTuple, type MantineThemeOverride, type VariantColorsResolver,
} from "@mantine/core";
import { paletteFor, type Palette, type PaletteKey } from "./palettes";

export const MONO = "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";
export const DISPLAY = "'Space Grotesk', ui-sans-serif, system-ui, -apple-system, sans-serif";

// Mantine needs a 10-shade tuple for the primary colour. The palettes only
// define two accent stops, so the rest are interpolated toward white (light
// shades) and toward the panel background (dark shades). Shade 6 — Mantine's
// default primaryShade — is the palette's `acc`, which is what every filled
// control ends up using.
function mix(a: string, b: string, t: number): string {
  const hex = (c: string) => {
    const h = c.replace("#", "");
    const v = h.length === 3 ? h.split("").map((x) => x + x).join("") : h;
    return [parseInt(v.slice(0, 2), 16), parseInt(v.slice(2, 4), 16), parseInt(v.slice(4, 6), 16)];
  };
  const [r1, g1, b1] = hex(a);
  const [r2, g2, b2] = hex(b);
  const ch = (x: number, y: number) => Math.round(x + (y - x) * t).toString(16).padStart(2, "0");
  return `#${ch(r1, r2)}${ch(g1, g2)}${ch(b1, b2)}`;
}

function accentTuple(p: Palette): MantineColorsTuple {
  return [
    mix(p.acc2, "#ffffff", 0.72),
    mix(p.acc2, "#ffffff", 0.54),
    mix(p.acc2, "#ffffff", 0.36),
    mix(p.acc2, "#ffffff", 0.18),
    p.acc2,
    mix(p.acc, p.acc2, 0.5),
    p.acc, // shade 6 — the default primary shade
    mix(p.acc, p.bg, 0.22),
    mix(p.acc, p.bg, 0.44),
    mix(p.acc, p.bg, 0.66),
  ] as unknown as MantineColorsTuple;
}

// Mantine resolves variant colours from its own palettes, which would drag the
// stock dark scale into every `variant="default"` control. This resolver points
// each variant at the terminal palette instead — the documented extension point
// for exactly this, and the reason no component needs per-usage colour props.
const vfVariantColorResolver: VariantColorsResolver = (input) => {
  const { variant, color } = input;
  const primary = !color || color === "vfAcc";

  if (color === "red") {
    // The mockup's destructive controls are a tinted panel, not solid red.
    if (variant === "filled") {
      return {
        background: "var(--vf-bad)", hover: "var(--vf-bad)",
        color: "var(--vf-bg)", border: "1px solid var(--vf-bad)",
      };
    }
    return {
      background: "var(--vf-badbg)", hover: "var(--vf-badbg)",
      color: "var(--vf-bad)", border: "1px solid var(--vf-badbd)",
    };
  }

  if (variant === "default") {
    return {
      background: "var(--vf-accbg)", hover: "var(--vf-hover)",
      color: "var(--vf-text)", border: "1px solid var(--vf-border)",
    };
  }
  if (primary && variant === "filled") {
    return {
      background: "var(--vf-acc)", hover: "var(--vf-acc2)",
      color: "var(--vf-bg)", border: "1px solid transparent",
    };
  }
  if (primary && variant === "light") {
    return {
      background: "var(--vf-accbg)", hover: "var(--vf-accbg)",
      color: "var(--vf-acc)", border: "1px solid var(--vf-border)",
    };
  }
  if (primary && variant === "outline") {
    return {
      background: "transparent", hover: "var(--vf-accbg)",
      color: "var(--vf-acc)", border: "1px solid var(--vf-acc)",
    };
  }
  if (primary && variant === "subtle") {
    return {
      background: "transparent", hover: "var(--vf-hover)",
      color: "var(--vf-acc)", border: "1px solid transparent",
    };
  }
  return defaultVariantColorsResolver(input);
};

// Mantine's built-in `dark` scale leaks into a few components regardless of the
// variant resolver; point it at the palette so nothing renders off-theme.
function darkTuple(p: Palette): MantineColorsTuple {
  return [
    p.text, p.sec, p.dim, p.dim2, p.border,
    p.border2, p.hover, p.panel, p.panel2, p.bg,
  ] as unknown as MantineColorsTuple;
}

// buildTheme returns the Mantine theme for one palette. The palette rides along
// in `theme.other` so cssVariablesResolver can turn it into CSS variables.
export function buildTheme(key: PaletteKey): MantineThemeOverride {
  const palette = paletteFor(key);
  return createTheme({
    // Every surface in the mockup is a hard-edged 1px box.
    defaultRadius: 0,
    focusRing: "auto",
    cursorType: "pointer",

    fontFamily: MONO,
    fontFamilyMonospace: MONO,
    headings: {
      fontFamily: DISPLAY,
      fontWeight: "700",
      sizes: {
        h1: { fontSize: "22px", lineHeight: "1.2" },
        h2: { fontSize: "19px", lineHeight: "1.2" },
        h3: { fontSize: "15px", lineHeight: "1.3" },
        h4: { fontSize: "14px", lineHeight: "1.3" },
        h5: { fontSize: "13px", lineHeight: "1.4" },
        h6: { fontSize: "12px", lineHeight: "1.4" },
      },
    },

    // The mockup's type ramp: 11 / 12.5 / 13 / 15 / 19.
    fontSizes: { xs: "11px", sm: "12.5px", md: "13px", lg: "15px", xl: "19px" },
    lineHeights: { xs: "1.4", sm: "1.45", md: "1.55", lg: "1.55", xl: "1.5" },
    spacing: { xs: "6px", sm: "10px", md: "14px", lg: "20px", xl: "26px" },
    radius: { xs: "0", sm: "0", md: "0", lg: "0", xl: "0" },

    primaryColor: "vfAcc",
    primaryShade: 6,
    colors: { vfAcc: accentTuple(palette), dark: darkTuple(palette) },
    variantColorResolver: vfVariantColorResolver,

    other: { palette, paletteKey: key },

    components: {
      Paper: { defaultProps: { radius: 0, withBorder: true, bg: "var(--vf-panel)" } },
      Button: { defaultProps: { radius: 0 } },
      ActionIcon: { defaultProps: { radius: 0 } },
      TextInput: { defaultProps: { radius: 0, size: "sm" } },
      PasswordInput: { defaultProps: { radius: 0, size: "sm" } },
      Textarea: { defaultProps: { radius: 0, size: "sm" } },
      NumberInput: { defaultProps: { radius: 0, size: "sm" } },
      NativeSelect: { defaultProps: { radius: 0, size: "sm" } },
      Select: { defaultProps: { radius: 0, size: "sm" } },
      MultiSelect: { defaultProps: { radius: 0, size: "sm" } },
      SegmentedControl: { defaultProps: { radius: 0, size: "sm" } },
      Badge: { defaultProps: { radius: 0 } },
      Progress: { defaultProps: { radius: 0 } },
      Modal: { defaultProps: { radius: 0, centered: true, transitionProps: { duration: 120 } } },
      Drawer: { defaultProps: { radius: 0, position: "right" } },
      Menu: { defaultProps: { radius: 0 } },
      Tooltip: { defaultProps: { radius: 0, fz: "xs" } },
      Card: { defaultProps: { radius: 0, withBorder: true } },
      Slider: { defaultProps: { radius: 0, size: "sm" } },
      Checkbox: { defaultProps: { radius: 0 } },
      Switch: { defaultProps: { radius: 0 } },
      Loader: { defaultProps: { type: "dots" } },
      Table: { defaultProps: { verticalSpacing: "xs", horizontalSpacing: "md", fz: "md" } },
      ScrollArea: {
        defaultProps: { scrollbarSize: 9, type: "auto" },
        styles: {
          thumb: { borderRadius: 0, background: "var(--vf-border)" },
          scrollbar: { background: "transparent", padding: 0 },
        },
      },
      Anchor: { defaultProps: { underline: "never" } },
    },
  });
}
